import { AttributeType, MoreFieldMappingItem } from 'src/interfaces/Template.interface'
import { CategoryMappingChangeData } from '../CategoryMappingEngine'
import {
  buildMoreMappingFormInitialValues,
  hasMoreMappingUserValue,
} from '../../utils/quicklistMoreMappingUtils'
import { buildCategoryAttributesFromRows } from './categorySuggestionAttributes'

export type ListingProfileAiMappingRow = {
  check_platform_field: string | number
  check_operator: string
  check_conjunction: string
  check_value: string
}

export type ListingProfileAiRequestPayload = {
  platform_id: number
  source_platform_id: number
  mapping_data: ListingProfileAiMappingRow[]
}

export type ListingProfileAiProductRecipeRow = {
  checkPlatformField?: string
  checkOperator?: string
  checkConjunction?: string
  checkValue?: string | null
}

export const buildListingProfileAiMappingData = (
  productRecipes: ListingProfileAiProductRecipeRow[] | undefined,
  fallbackCheckPlatformField: string
): ListingProfileAiMappingRow[] => {
  if (!productRecipes?.length) return []
  return productRecipes.map((item) => ({
    check_platform_field:
      item?.checkPlatformField === '' || item?.checkPlatformField == null
        ? fallbackCheckPlatformField
        : item.checkPlatformField,
    check_operator: item?.checkOperator ?? 'CONTAINS',
    check_conjunction: item?.checkConjunction ?? 'OR',
    check_value: item?.checkValue ?? '',
  }))
}

export const validateListingProfileAiRequest = (input: {
  destinationPlatformId?: number | null
  sourcePlatformId?: number | null
  mappingData: ListingProfileAiMappingRow[]
}): string | null => {
  if (!input.destinationPlatformId) {
    return 'Select a target channel before using AI Auto-Map.'
  }
  if (!input.sourcePlatformId) {
    return 'Primary source store is not connected. AI Auto-Map requires a source channel.'
  }
  if (!input.mappingData.length) {
    return 'Add at least one product sync filter before using AI Auto-Map.'
  }
  const hasInvalidRow = input.mappingData.some(
    (row) =>
      row.check_platform_field === '' ||
      row.check_platform_field == null ||
      !String(row.check_value ?? '').trim()
  )
  if (hasInvalidRow) {
    return 'Complete all product sync filter rows (field and value) before using AI Auto-Map.'
  }
  return null
}

export const extractThunkFailureMessage = (result: unknown): string | null => {
  const r = result as {
    meta?: { requestStatus?: string }
    payload?: unknown
    error?: { message?: string }
  }
  if (r?.meta?.requestStatus !== 'rejected') return null
  const payload = r.payload as Record<string, unknown> | string | undefined
  if (typeof payload === 'string' && payload.trim()) return payload
  if (payload && typeof payload === 'object') {
    const data = payload.data as Record<string, unknown> | undefined
    const candidates = [
      payload.message,
      payload.error,
      data?.message,
      data?.error,
      data?.detail,
    ]
    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) return c
    }
  }
  return r.error?.message ?? 'AI Auto-Map request failed.'
}

export const extractApiPayloadErrorMessage = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') return null
  const p = payload as Record<string, unknown>
  const data = p.data as Record<string, unknown> | undefined
  const candidates = [p.message, p.error, data?.message, data?.error, data?.detail]
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c
  }
  return null
}

/** AI misc placeholders reported in staging (exact label match, case-insensitive). */
const AI_MISC_PLACEHOLDER_LABEL = /^(unknown|not specified|n\/a|na)$/i

const trimAiMiscLabel = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

export const isAiMiscPlaceholderLabel = (label: unknown): boolean => {
  const trimmed = trimAiMiscLabel(label)
  if (!trimmed) return false
  return AI_MISC_PLACEHOLDER_LABEL.test(trimmed)
}

const labelFromAiMiscMappingRef = (ref: unknown): string => {
  if (!ref || typeof ref !== 'object') return ''
  const row = ref as { display_name?: string; api_name?: string; name?: string }
  return (
    trimAiMiscLabel(row.display_name) ||
    trimAiMiscLabel(row.api_name) ||
    trimAiMiscLabel(row.name)
  )
}

const aiMiscTemplateRowHasPlaceholder = (row: unknown): boolean => {
  if (!row || typeof row !== 'object') return false
  const rec = row as Record<string, unknown>
  if (isAiMiscPlaceholderLabel(rec.value_str)) return true
  if (isAiMiscPlaceholderLabel(labelFromAiMiscMappingRef(rec.mapped_field))) return true
  if (isAiMiscPlaceholderLabel(labelFromAiMiscMappingRef(rec.value))) return true
  return false
}

/** True when the API row is a known “no real data” placeholder (before form hydrate). */
export const isAiMiscSuggestionPlaceholder = (suggestion: Record<string, unknown>): boolean => {
  if (isAiMiscPlaceholderLabel(suggestion.value_str)) return true
  if (isAiMiscPlaceholderLabel(labelFromAiMiscMappingRef(suggestion.mapped_field))) return true
  if (isAiMiscPlaceholderLabel(labelFromAiMiscMappingRef(suggestion.value))) return true

  const templateData = suggestion.template_data
  if (!Array.isArray(templateData)) return false
  return templateData.some(aiMiscTemplateRowHasPlaceholder)
}

/**
 * Same bar as save: skip empty rows and AI placeholder labels (Unknown / Not specified / NA / N/A).
 */
export const shouldApplyMiscAiSuggestion = (
  fieldMeta: MoreFieldMappingItem,
  suggestion: Record<string, unknown>
): boolean => {
  if (isAiMiscSuggestionPlaceholder(suggestion)) return false

  const values = buildMoreMappingFormInitialValues(fieldMeta, suggestion, null)
  return hasMoreMappingUserValue(values, fieldMeta)
}

const resolvePlatformFieldIdFromSuggestion = (suggestion: {
  platform_field?: { id?: number } | number | null
}): number | null => {
  const pf = suggestion.platform_field
  if (pf == null) return null
  if (typeof pf === 'object') {
    const id = Number(pf.id)
    return Number.isFinite(id) ? id : null
  }
  const id = Number(pf)
  return Number.isFinite(id) ? id : null
}

/** Match misc suggestion rows to grid fields (same rules as edit hydrate). */
export const mergeMiscMappingSuggestions = (
  prevList: MoreFieldMappingItem[],
  suggestions: unknown[]
): { nextList: MoreFieldMappingItem[]; appliedCount: number } => {
  if (!suggestions?.length || !prevList.length) {
    return { nextList: prevList, appliedCount: 0 }
  }

  let appliedCount = 0
  const nextList = prevList.map((item) => {
    const match = suggestions.find((raw) => {
      const s = raw as {
        platform_field?: { id?: number } | number | null
      }
      const suggestionFieldId = resolvePlatformFieldIdFromSuggestion(s)
      if (suggestionFieldId == null) return false
      return (
        Number(suggestionFieldId) === Number(item.id) ||
        Number(suggestionFieldId) === Number(item.platform_field?.id)
      )
    }) as Record<string, unknown> | undefined

    if (!match) return item
    if (!shouldApplyMiscAiSuggestion(item, match)) return item

    const existingTemplateId = item.moreMappingData?.id ?? null
    appliedCount += 1
    return {
      ...item,
      moreMappingData: {
        ...(match as object),
        id: existingTemplateId,
      },
    }
  })

  return { nextList, appliedCount }
}

export type HydrateCategoryFromTemplateDeps = {
  /** Optional ? same as edit hydrate via getSelectedCategoryTreeAsync */
  fetchCategoryPathLabel?: (categoryId: number) => Promise<string | null>
  setCategoryUpdateData: (value: unknown) => void
  setInitialCategoryState: (value: {
    categoryId: number | null
    categoryPath: string
    platformFieldId: number | null
  }) => void
  setEngineState: (value: CategoryMappingChangeData) => void
  setEngineKey: (updater: (prev: number) => number) => void
  setSelectedAttributes: (attributes: unknown[]) => void
  /** Attribute field objects from payload. Empty array clears a previous preloaded catalog. */
  setPreloadedAttributeCatalog?: (attributes: AttributeType[]) => void
}

/**
 * Apply category template payload (GET template or AI suggestion) into listing profile form state.
 * Mirrors quickListEditUtils category hydrate.
 */
export async function hydrateCategoryMappingFromTemplatePayload(
  deps: HydrateCategoryFromTemplateDeps,
  catData: unknown
): Promise<boolean> {
  if (!catData || typeof catData !== 'object') return false
  const data = catData as {
    category_data?: { id?: number; api_name?: string; display_name?: string; name?: string; platform_field?: number } | number
    platform_field?: number | { id?: number }
    attributes?: unknown[]
  }

  if (data.category_data == null) return false

  deps.setCategoryUpdateData(data)

  const cData = data.category_data
  const cId = typeof cData === 'object' ? cData?.id : cData
  if (cId == null || !Number.isFinite(Number(cId))) return false

  let fullPath =
    (typeof cData === 'object'
      ? cData?.api_name ?? cData?.display_name ?? cData?.name
      : '') ?? ''

  if (deps.fetchCategoryPathLabel) {
    try {
      const pathFromTree = await deps.fetchCategoryPathLabel(Number(cId))
      if (pathFromTree?.trim()) {
        fullPath = pathFromTree
      }
    } catch (err) {
      console.error('Failed to fetch full category path for AI suggestion:', err)
    }
  }

  const platformFieldId =
    (typeof cData === 'object' ? cData.platform_field : null) ??
    (typeof data.platform_field === 'object'
      ? data.platform_field?.id ?? null
      : data.platform_field ?? null)

  deps.setInitialCategoryState({
    categoryId: Number(cId),
    categoryPath: fullPath,
    platformFieldId:
      platformFieldId != null && Number.isFinite(Number(platformFieldId))
        ? Number(platformFieldId)
        : null,
  })

  deps.setEngineState({
    categoryId: Number(cId),
    categoryPath: fullPath,
    isLeafCategory: true,
    showAttributes: false,
    isAttributeLoading: false,
  })

  if (deps.setPreloadedAttributeCatalog) {
    deps.setPreloadedAttributeCatalog(
      Array.isArray(data.attributes)
        ? buildCategoryAttributesFromRows(data.attributes)
        : []
    )
  }

  deps.setEngineKey((prev) => prev + 1)

  if (data.attributes && Array.isArray(data.attributes)) {
    deps.setSelectedAttributes(data.attributes)
  }

  return true
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value != null && typeof value === 'object' ? (value as Record<string, unknown>) : null

/** Misc AI: supports `{ results }` and API envelope `{ data: { results } }`. */
export const extractMiscMappingResults = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload

  const root = asRecord(payload)
  if (!root) return []

  if (Array.isArray(root.results)) return root.results as unknown[]

  const nested = asRecord(root.data)
  if (nested && Array.isArray(nested.results)) return nested.results as unknown[]

  return []
}

/** Category AI: supports bare template object and `{ data: { category_data, ... } }`. */
export const extractCategorySuggestionPayload = (payload: unknown): unknown => {
  const root = asRecord(payload)
  if (!root) return payload

  const nested = asRecord(root.data)
  if (nested?.category_data != null) return nested
  if (root.category_data != null) return root

  return payload
}

/** Queue poll for POST /api/get-category-suggestion (206 until 200). */
export const CATEGORY_SUGGESTION_POLL_INTERVAL_MS = 10_000
export const CATEGORY_SUGGESTION_POLL_MAX_ATTEMPTS = 50

export const extractCategorySuggestionQueueId = (payload: unknown): string | null => {
  const root = asRecord(payload)
  if (!root) return null
  const id = root.queue_id
  return typeof id === 'string' && id.trim() ? id.trim() : null
}

export const hasCategorySuggestionResult = (payload: unknown): boolean => {
  const catPayload = extractCategorySuggestionPayload(payload)
  const rec = asRecord(catPayload)
  if (!rec || rec.category_data == null) return false

  const cData = rec.category_data
  const cId = typeof cData === 'object' ? (cData as { id?: number }).id : cData
  return cId != null && Number.isFinite(Number(cId))
}

export const isCategorySuggestionQueuePending = (payload: unknown): boolean => {
  const queueId = extractCategorySuggestionQueueId(payload)
  if (!queueId) return false
  return !hasCategorySuggestionResult(payload)
}

/** API errors only — not 206 pending or 200 success envelopes with category_data. */
export const extractCategorySuggestionErrorMessage = (payload: unknown): string | null => {
  if (isCategorySuggestionQueuePending(payload)) return null
  if (hasCategorySuggestionResult(payload)) return null
  return extractApiPayloadErrorMessage(payload)
}

export const sleepMs = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
